import { setMessagesFulfilled } from '../../actions/messages';
import { currentOpenChatSelector } from '../../selectors';
import { fetchHistoryMessages } from '../../services/fetchHistoryMessages';
import { hideAlert, showAlert } from '../../slices/alertSlice';
import { IApplicationContext } from '../../types/applicationContext';
import { TMessageSchema } from '../../types/messages';
import { TDispatch, TGetState } from '../../types/redux/state';

enum ELoadHistoryStatus {
  Succeed = 'succeed',
  Failed = 'failed',
  Cancelled = 'cancelled',
}

export type TReplyScrollStoreParams = {
  chatId: string;
  dispatchAppStore: TDispatch;
  getAppState: TGetState;
  appContext: IApplicationContext;
};

export class ReplyScrollStore {
  private isProcessing = false;

  private replyTargetMessageId: string | null = null;

  private hasLoadedHistoryPages = false;

  private abortController: AbortController | null = null;

  private readonly listeners = new Set<VoidFunction>();

  private readonly chatId: string;

  private readonly dispatchAppStore: TDispatch;

  private readonly getAppState: TGetState;

  private readonly appContext: IApplicationContext;

  public constructor({ chatId, dispatchAppStore, getAppState, appContext }: TReplyScrollStoreParams) {
    this.chatId = chatId;
    this.dispatchAppStore = dispatchAppStore;
    this.getAppState = getAppState;
    this.appContext = appContext;
  }

  public readonly getReplyTargetMessageId = (): string | null => this.replyTargetMessageId;

  public readonly didLoadHistoryPages = (): boolean => this.hasLoadedHistoryPages;

  public readonly isReplyScrollProcessing = (): boolean => this.isProcessing;

  public readonly subscribe = (onStoreChange: VoidFunction): VoidFunction => {
    this.listeners.add(onStoreChange);

    return () => {
      this.listeners.delete(onStoreChange);
    };
  };

  public readonly scrollToReplyTargetMessage = async (messageId: string): Promise<void> => {
    if (this.isProcessing) {
      return;
    }

    this.setIsReplyScrollProcessing(true);
    this.hasLoadedHistoryPages = false;
    this.setReplyTargetMessageId(null);

    const status = await this.loadHistoryUntilMessage(messageId);

    if (status === ELoadHistoryStatus.Cancelled) {
      return;
    }

    this.setIsReplyScrollProcessing(false);

    if (status === ELoadHistoryStatus.Succeed) {
      this.setReplyTargetMessageId(messageId);
    }

    if (status === ELoadHistoryStatus.Failed) {
      this.dispatchAppStore(
        showAlert({
          message: 'Не удалось загрузить сообщение',
          button: {
            text: 'Повторить',
            onClick: () => {
              this.dispatchAppStore(hideAlert());
              this.scrollToReplyTargetMessage(messageId);
            },
          },
        }),
      );
    }
  };

  public readonly cancelHistoryLoading = (): void => {
    this.abortController?.abort();
    this.abortController = null;
    this.setIsReplyScrollProcessing(false);
  };

  public readonly clearReplyTargetMessage = (): void => {
    this.hasLoadedHistoryPages = false;
    this.setReplyTargetMessageId(null);
  };

  private async loadHistoryUntilMessage(messageId: string): Promise<ELoadHistoryStatus> {
    const {
      custom: { messagesMap },
    } = this.appContext;

    const abortController = new AbortController();
    this.abortController = abortController;
    const { signal } = abortController;

    while (!messagesMap.has(messageId)) {
      const appState = this.getAppState();
      const { allMessagesLoaded, currentTimestamp } = currentOpenChatSelector(appState) || {};

      /**
       * Почти нереальный кейс, когда при клике на цитату реплая
       * все сообщения загружены, но исходного сообщения из реплая нет в списке сообщений
       */
      if (allMessagesLoaded) {
        return ELoadHistoryStatus.Failed;
      }

      const createdAt = currentTimestamp ? new Date(currentTimestamp).toISOString() : undefined;

      try {
        const { response, error } = await fetchHistoryMessages(this.appContext, {
          parameters: { chatId: this.chatId, createdAt },
          signal,
          causedByReplyScroll: true,
        });

        /** Отмену нужно проверять до ошибки: fetchHistoryMessages гасит AbortError и отдаёт его как обычную ошибку */
        if (signal.aborted) {
          return ELoadHistoryStatus.Cancelled;
        }

        if (error || !response) {
          return ELoadHistoryStatus.Failed;
        }

        this.dispatchAppStore(
          setMessagesFulfilled({
            chatId: this.chatId,
            messages: response.messages as unknown as TMessageSchema[],
            messagesMap,
          }),
        );

        this.hasLoadedHistoryPages = true;

        /**
         * Аналогично кейсу выше, если больше нет сообщения для загрузки,
         * но искомого сообщения так и нет в списке сообщений
         */
        if (!response.canLoadMore && !messagesMap.has(messageId)) {
          return ELoadHistoryStatus.Failed;
        }
      } catch {
        if (signal.aborted) {
          return ELoadHistoryStatus.Cancelled;
        }

        return ELoadHistoryStatus.Failed;
      }
    }

    return ELoadHistoryStatus.Succeed;
  }

  private setIsReplyScrollProcessing(isProcessing: boolean): void {
    if (this.isProcessing === isProcessing) {
      return;
    }

    this.isProcessing = isProcessing;
    this.notifySubscribers();
  }

  private setReplyTargetMessageId(messageId: string | null): void {
    if (this.replyTargetMessageId === messageId) {
      return;
    }

    this.replyTargetMessageId = messageId;
    this.notifySubscribers();
  }

  private notifySubscribers(): void {
    this.listeners.forEach(listener => listener());
  }
}
